import type { Difficulty } from '../bot/types';
import { POWERUPS, powerIsFree } from '../powerups';
import { CRITTER_GATES } from './gates';
import type { RoadNode, StreakMark, TrophyBand } from './types';

export { CRITTER_GATES };

export const CLOCK_SKEW_MS = 120_000;
export const SETTLED_CAP = 32;
export const LEDGER_CAP = 64;
export const CLOSED_CAP = 8;

export const UNLOCK_PER_COST = 40;
export const UPGRADE_L2 = 100;
export const UPGRADE_L3 = 250;
export const RIBBON_HONEY = 40;

export const MATCH_ACORNS = { win: 40, loss: 16, tie: 24 } as const;

/** Planning rates for the day table. Playtests can replace these later. */
export const CASUAL_WIN_RATE: Record<Difficulty, number> = {
  easy: 0.75,
  normal: 0.58,
  hard: 0.45,
};

/**
 * First glen is slow on purpose: a casual day clears one early node, not the
 * whole opening. Night water and High glen stay a small gain at a 45% win rate,
 * and a slower gain at a shaky 33%.
 */
export const TROPHY_BANDS: readonly TrophyBand[] = [
  { name: 'First glen', min: 0, bot: 'easy', win: 8, loss: 0, floor: 0 },
  { name: 'Thistle path', min: 80, bot: 'easy', win: 8, loss: 8, floor: 40 },
  { name: 'Deep wood', min: 220, bot: 'normal', win: 10, loss: 6, floor: 160 },
  { name: 'Night water', min: 400, bot: 'hard', win: 14, loss: 6, floor: 320 },
  { name: 'High glen', min: 700, bot: 'hard', win: 12, loss: 5, floor: 560 },
];

/**
 * The 80, 220, and 400 purses sit between their neighbors. Those stops used to
 * unlock Bramble, Cleo, and Otto. The cast starts unlocked, so the stops pay
 * acorns only. Later critters can take new stops.
 */
const REWARD_NODES: readonly RoadNode[] = [
  { at: 20, acorns: 80, honey: 0 },
  { at: 50, acorns: 120, honey: 0 },
  { at: 80, acorns: 140, honey: 0 },
  { at: 120, acorns: 160, honey: 0 },
  { at: 160, acorns: 0, honey: 10 },
  { at: 220, acorns: 180, honey: 0 },
  { at: 280, acorns: 200, honey: 0 },
  { at: 340, acorns: 0, honey: 10 },
  { at: 400, acorns: 220, honey: 0 },
  { at: 480, acorns: 240, honey: 0 },
  { at: 560, acorns: 0, honey: 20 },
  { at: 640, acorns: 320, honey: 0 },
  { at: 760, acorns: 0, honey: 20 },
  { at: 900, acorns: 400, honey: 0 },
  { at: 1100, acorns: 0, honey: 30 },
];

/**
 * New critters take their own stops. Each mark is unused by a trick or a purse,
 * because a second node at the same trophy is skipped.
 */
const CRITTER_STOPS: readonly RoadNode[] = [
  { at: 100, acorns: 0, honey: 0, critter: 'bruno' },
  { at: 180, acorns: 0, honey: 0, critter: 'tuck' },
  { at: 230, acorns: 0, honey: 0, critter: 'thistle' },
  { at: 310, acorns: 0, honey: 0, critter: 'marlow' },
  { at: 410, acorns: 0, honey: 0, critter: 'wren' },
  { at: 490, acorns: 0, honey: 0, critter: 'juniper' },
  { at: 590, acorns: 0, honey: 0, critter: 'lumi' },
  { at: 690, acorns: 0, honey: 0, critter: 'barley' },
  { at: 750, acorns: 0, honey: 0, critter: 'fern' },
  { at: 880, acorns: 0, honey: 0, critter: 'posy' },
  { at: 1060, acorns: 0, honey: 0, critter: 'dash' },
];

/** Reward stops stay put. Each paid trick is its own stop and grants 5 copies. Free tricks are not stops. */
export const ROAD: readonly RoadNode[] = [
  ...REWARD_NODES,
  ...POWERUPS.filter((power) => !powerIsFree(power) && power.unlockAt >= 0).map((power) => ({
    at: power.unlockAt,
    acorns: 0,
    honey: 0,
    trick: power.id,
  })),
  ...CRITTER_STOPS,
].sort((a, b) => a.at - b.at);

export const STREAK_MARKS: readonly StreakMark[] = [
  { wins: 1, acorns: 40, honey: 0 },
  { wins: 2, acorns: 50, honey: 0 },
  { wins: 3, acorns: 80, honey: 0 },
  { wins: 5, acorns: 280, honey: 10, role: 'headline' },
  { wins: 7, acorns: 90, honey: 0 },
  { wins: 10, acorns: 140, honey: 20, role: 'bonus' },
];
